import React, { useState, useEffect, useRef } from 'react'
import { useParams, useHistory } from 'react-router-dom'
import {
    FormGroup,
    Label,
    Input,
    Button,
    Card,
    CardHeader,
    CardBody,
    CardFooter,
    CardTitle,
    Row,
    Col,
    Table,
    Dropdown,
    DropdownToggle,
    DropdownMenu,
    DropdownItem
} from "reactstrap";
import Swal from 'sweetalert2/dist/sweetalert2.js';
import 'sweetalert2/src/sweetalert2.scss';
import moment from 'moment'
import OrderService from '../../services/order.service'
import Deco from '../../security/deco'
import './CSS.css'

function OrderIDDetails() {
    const { id } = useParams();
    const history = useHistory();
    let userRole = '';
    try { userRole = JSON.parse(Deco(localStorage.getItem('user')))?.role || ''; } catch (e) { }
    const [orderData, setOrderData] = useState(null);
    const [toolsData, setToolsData] = useState(null);
    const [botActionData, setBotActionData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [updatingStatus, setUpdatingStatus] = useState(false);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [savingCustomer, setSavingCustomer] = useState(false);
    const [editedCustomer, setEditedCustomer] = useState({});
    const originalCustomerRef = useRef({});
    const dropdownRef = useRef(null);
    const abortControllerRef = useRef(null);

    useEffect(() => {
        fetchOrderDetails();

        return () => {
            // Hủy request nếu component unmount
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, []);

    useEffect(() => {
        function handleClickOutside(event) {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setDropdownOpen(false);
            }
        }
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [dropdownRef]);

    const toggleDropdown = () => setDropdownOpen(!dropdownOpen);

    const fetchOrderDetails = async () => {
        try {
            // Hủy request cũ nếu có
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }

            // Tạo AbortController mới
            abortControllerRef.current = new AbortController();
            const signal = abortControllerRef.current.signal;

            setLoading(true);
            const response = await OrderService.getOrderById(id, signal);
            setOrderData(response.order);
            setToolsData(response.order.tools || []);
            setBotActionData(response.order.botAction || []);
            // Snapshot dữ liệu gốc để track thay đổi
            const original = {
                email: response.order.email || '',
                fullName: response.order.fullName || '',
                phone: response.order.phone || '',
                note: response.order.note || '',
                invoiceOrganization: response.order.invoiceInfo?.organization || '',
                invoiceTaxCode: response.order.invoiceInfo?.taxCode || '',
                invoiceIdentificationNumber: response.order.invoiceInfo?.identificationNumber || '',
                invoiceAddress: [response.order.invoiceInfo?.address, response.order.invoiceInfo?.wardName, response.order.invoiceInfo?.locationName].filter(Boolean).join(', '),
            };
            originalCustomerRef.current = original;
            setEditedCustomer(original);
            setLoading(false);
        } catch (err) {
            // Không hiển thị lỗi nếu request bị hủy
            if (err.name === 'AbortError') {
                console.log('Request aborted');
                return;
            }

            setError('Không thể tải thông tin đơn hàng');
            setLoading(false);
            Swal.fire({
                icon: 'error',
                title: 'Lỗi',
                text: 'Không thể tải thông tin đơn hàng',
            });
        }
    };

    // Track thay đổi customer fields
    const handleCustomerChange = (field, value) => {
        setEditedCustomer(prev => ({ ...prev, [field]: value }));
    };

    const hasCustomerChanges = () => {
        const orig = originalCustomerRef.current;
        return Object.keys(orig).some(key => (editedCustomer[key] || '') !== (orig[key] || ''));
    };

    const handleSaveCustomer = async () => {
        if (!hasCustomerChanges()) return;
        setSavingCustomer(true);
        try {
            const data = {};
            const orig = originalCustomerRef.current;
            // Customer fields
            ['email', 'fullName', 'phone', 'note'].forEach(key => {
                if ((editedCustomer[key] || '') !== (orig[key] || '')) {
                    data[key] = editedCustomer[key];
                }
            });
            // Invoice fields
            const invoiceChanges = {};
            if ((editedCustomer.invoiceOrganization || '') !== (orig.invoiceOrganization || '')) invoiceChanges.organization = editedCustomer.invoiceOrganization;
            if ((editedCustomer.invoiceTaxCode || '') !== (orig.invoiceTaxCode || '')) invoiceChanges.taxCode = editedCustomer.invoiceTaxCode;
            if ((editedCustomer.invoiceIdentificationNumber || '') !== (orig.invoiceIdentificationNumber || '')) invoiceChanges.identificationNumber = editedCustomer.invoiceIdentificationNumber;
            if ((editedCustomer.invoiceAddress || '') !== (orig.invoiceAddress || '')) invoiceChanges.address = editedCustomer.invoiceAddress;
            if (Object.keys(invoiceChanges).length > 0) data.invoiceInfo = invoiceChanges;
            const result = await OrderService.updateCustomerInfo(id, data);
            if (result.success) {
                Swal.fire({
                    icon: 'success', title: 'Thành công', text: result.message, timer: 1500, showConfirmButton: false,
                    didOpen: () => { const popup = Swal.getPopup(); popup.style.borderRadius = '30px'; }
                });
                fetchOrderDetails(); // Reload data
            } else {
                Swal.fire({
                    icon: 'error', title: 'Lỗi', text: result.message,
                    didOpen: () => { const popup = Swal.getPopup(); popup.style.borderRadius = '30px'; }
                });
            }
        } catch (err) {
            Swal.fire({
                icon: 'error', title: 'Lỗi', text: 'Không thể cập nhật thông tin',
                didOpen: () => { const popup = Swal.getPopup(); popup.style.borderRadius = '30px'; }
            });
        } finally {
            setSavingCustomer(false);
        }
    };

    const handleUpdateStatus = async (newStatus) => {
        if (newStatus === orderData?.paymentStatus) return;

        try {
            // Nếu chuyển sang trạng thái "Đã thanh toán", hiển thị hộp thoại xác nhận
            if (newStatus === "Đã thanh toán") {
                const confirmResult = await Swal.fire({
                    title: 'Xác nhận thanh toán',
                    text: 'Bạn có muốn tạo tài khoản và gửi email cho khách hàng không?',
                    icon: 'question',
                    showCancelButton: true,
                    confirmButtonColor: '#6bd098',
                    cancelButtonColor: '#ef8157',
                    confirmButtonText: 'Có, tạo và gửi email',
                    cancelButtonText: 'Không',
                    allowOutsideClick: false,
                    customClass: {
                        popup: 'swal-custom-popup'
                    },
                    didOpen: () => {
                        // Thêm style cho borderRadius
                        const popup = Swal.getPopup();
                        popup.style.borderRadius = '30px';
                        popup.style.boxShadow = '0 10px 30px rgba(0, 0, 0, 0.2)';
                    }
                });

                // Nếu người dùng không xác nhận, thoát khỏi hàm
                if (!confirmResult.isConfirmed) {
                    return;
                }
            }

            setUpdatingStatus(true);

            // Hiển thị Swal với spinner - lưu tham chiếu để cập nhật sau
            const loadingSwal = Swal.fire({
                title: 'Đang xử lý',
                text: 'Đang cập nhật trạng thái đơn hàng...',
                allowOutsideClick: false,
                allowEscapeKey: false,
                showConfirmButton: false,
                customClass: {
                    popup: 'swal-custom-popup'
                },
                didOpen: () => {
                    Swal.showLoading();

                    // Thêm style cho borderRadius
                    const popup = Swal.getPopup();
                    popup.style.borderRadius = '30px';
                    popup.style.boxShadow = '0 10px 30px rgba(0, 0, 0, 0.2)';
                }
            });

            const response = await OrderService.updateOrder(id, newStatus);

            if (response.success) {
                // Fetch lại dữ liệu đơn hàng từ server thay vì chỉ cập nhật trạng thái cục bộ
                await fetchOrderDetails();

                // Cập nhật Swal hiện tại thay vì tạo mới
                Swal.update({
                    icon: 'success',
                    title: 'Cập nhật thành công',
                    text: response.message || 'Trạng thái đơn hàng đã được cập nhật',
                    showConfirmButton: true,
                    confirmButtonText: 'Đóng',
                    timer: 1500,
                    allowOutsideClick: true
                });

                // Đảm bảo spinner sẽ bị ẩn
                Swal.hideLoading();
            } else {
                // Cập nhật Swal hiện tại thay vì tạo mới
                Swal.update({
                    icon: 'error',
                    title: 'Lỗi',
                    text: response.message || 'Không thể cập nhật trạng thái đơn hàng',
                    showConfirmButton: true,
                    confirmButtonText: 'Đóng',
                    allowOutsideClick: true
                });

                // Đảm bảo spinner sẽ bị ẩn
                Swal.hideLoading();
            }
        } catch (err) {
            // Cập nhật Swal hiện tại thay vì tạo mới
            Swal.update({
                icon: 'error',
                title: 'Lỗi',
                text: err.message || 'Không thể cập nhật trạng thái đơn hàng',
                showConfirmButton: true,
                confirmButtonText: 'Đóng',
                allowOutsideClick: true
            });

            // Đảm bảo spinner sẽ bị ẩn
            Swal.hideLoading();
        } finally {
            setUpdatingStatus(false);
            setDropdownOpen(false);
        }
    };

    function back() {
        // Hủy request nếu đang loading và người dùng click nút quay lại
        if (loading && abortControllerRef.current) {
            abortControllerRef.current.abort();
        }
        window.history.back();
    }

    function getStatusColor(status) {
        if (status === "Đã thanh toán") return "#6bd098";
        if (status === "Chưa thanh toán") return "#fbc658";
        if (status === "Đã hủy") return "#ef8157";
        return "#ef8157";
    }

    function getActionStatusColor(status) {
        // Kiểm tra nếu trạng thái thành công hoặc các từ tích cực
        if (status && (
            status.toLowerCase().includes('thành công') ||
            status.toLowerCase().includes('hoàn thành') ||
            status.toLowerCase().includes('success') ||
            status.toLowerCase().includes('completed')
        )) {
            return "#6bd098"; // success color
        }
        // Kiểm tra nếu trạng thái thất bại hoặc các từ tiêu cực
        else if (status && (
            status.toLowerCase().includes('thất bại') ||
            status.toLowerCase().includes('lỗi') ||
            status.toLowerCase().includes('hủy') ||
            status.toLowerCase().includes('failed') ||
            status.toLowerCase().includes('error')
        )) {
            return "#ef8157"; // danger color
        }
        return "inherit"; // default color
    }

    function getActionStatusBgColor(status) {
        // Màu nền cho trạng thái thành công
        if (status && (
            status.toLowerCase().includes('thành công') ||
            status.toLowerCase().includes('hoàn thành') ||
            status.toLowerCase().includes('success') ||
            status.toLowerCase().includes('completed')
        )) {
            return "rgba(107, 208, 152, 0.15)"; // success background with opacity
        }
        // Màu nền cho trạng thái thất bại
        else if (status && (
            status.toLowerCase().includes('thất bại') ||
            status.toLowerCase().includes('lỗi') ||
            status.toLowerCase().includes('hủy') ||
            status.toLowerCase().includes('failed') ||
            status.toLowerCase().includes('error')
        )) {
            return "rgba(239, 129, 87, 0.15)"; // danger background with opacity
        }
        return "transparent"; // default background
    }

    function getStatusOptions() {
        if (orderData?.paymentStatus === "Đã thanh toán") {
            return [
                { value: "Đã thanh toán", label: "Đã thanh toán", color: "#6bd098" }
            ];
        } else if (orderData?.paymentStatus === "Đã hủy") {
            return [
                { value: "Đã hủy", label: "Đã hủy", color: "#ef8157" }
            ];
        } else {
            return [
                { value: "Chưa thanh toán", label: "Chưa thanh toán", color: "#fbc658" },
                { value: "Đã thanh toán", label: "Đã thanh toán", color: "#6bd098" },
                { value: "Đã hủy", label: "Đã hủy", color: "#ef8157" }
            ];
        }
    }

    // Hiển thị nút quay lại trong mọi trường hợp
    const renderBackButton = () => (
        <Button
            style={{ marginBottom: '30px', borderRadius: '30px' }}
            color="info"
            onClick={back}>
            &laquo; Quay lại
        </Button>
    );

    // Hiển thị spinner trung tâm trang khi đang loading
    const renderLoading = () => (
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 'calc(100vh - 200px)' }}>
            <div className="text-center">
                <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
                    <span className="sr-only">Đang tải...</span>
                </div>
            </div>
        </div>
    );

    if (error) {
        return (
            <div className="content">
                {renderBackButton()}
                <Card>
                    <CardBody className="text-center">
                        <h4 className="text-danger">{error}</h4>
                        <Button color="primary" onClick={fetchOrderDetails}>Thử lại</Button>
                    </CardBody>
                </Card>
            </div>
        );
    }

    return (
        <div className="content">
            {renderBackButton()}

            {loading ? (
                renderLoading()
            ) : (
                <>
                    <Row>
                        <Col lg="4" md="4" sm="4">
                            <Card style={{ padding: '15px', borderRadius: '30px' }}>
                                <CardHeader>
                                    <Label style={{ fontSize: '24px', color: 'DimGray', fontWeight: 'bold' }}><i className="fas fa-user" style={{ marginRight: '8px' }}></i>Khách hàng</Label>
                                </CardHeader>

                                <CardBody>
                                    <FormGroup>
                                        <div className="form-row">
                                            <div className="form-group col-md-12">
                                                <Label for="email">Email</Label>
                                                <Input
                                                    value={editedCustomer.email || ''}
                                                    id="email"
                                                    onChange={(e) => handleCustomerChange('email', e.target.value)}
                                                />
                                            </div>
                                        </div>

                                        <div className="form-row">
                                            <div className="form-group col-md-12">
                                                <Label for="fullname">Họ và tên</Label>
                                                <Input
                                                    value={editedCustomer.fullName || ''}
                                                    id="fullname"
                                                    onChange={(e) => handleCustomerChange('fullName', e.target.value)}
                                                />
                                            </div>
                                        </div>

                                        <div className="form-row">
                                            <div className="form-group col-md-12">
                                                <Label for="phone">Số điện thoại</Label>
                                                <Input
                                                    value={editedCustomer.phone || ''}
                                                    id="phone"
                                                    onChange={(e) => handleCustomerChange('phone', e.target.value)}
                                                />
                                            </div>
                                        </div>

                                        <div className="form-row">
                                            <div className="form-group col-md-12">
                                                <Label for="note"><i className="fas fa-sticky-note" style={{ marginRight: '6px' }}></i>Ghi chú</Label>
                                                <Input
                                                    type="textarea"
                                                    value={editedCustomer.note || ''}
                                                    id="note"
                                                    onChange={(e) => handleCustomerChange('note', e.target.value)}
                                                    style={{ minHeight: '60px', resize: 'none' }}
                                                />
                                            </div>
                                        </div>

                                        {orderData?.couponCode && (
                                            <div className="form-row">
                                                <div className="form-group col-md-12">
                                                    <Label><i className="fas fa-ticket-alt" style={{ marginRight: '6px' }}></i>Mã giảm giá</Label>
                                                    <div style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '10px',
                                                        backgroundColor: '#f0f9f4',
                                                        border: '1px solid #6bd098',
                                                        borderRadius: '10px',
                                                        padding: '10px 15px'
                                                    }}>
                                                        <span style={{ fontWeight: 'bold', color: '#6bd098', fontSize: '16px' }}>{orderData.couponCode}</span>
                                                        {orderData.couponDiscount > 0 && (
                                                            <span style={{ color: '#888' }}>(-{new Intl.NumberFormat('vi-VN').format(orderData.couponDiscount)} đ)</span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </FormGroup>

                                    {orderData?.invoiceRequested && orderData?.invoiceInfo && (
                                        <>
                                            <hr style={{ margin: '15px 0' }} />
                                            <div style={{ marginBottom: '10px' }}>
                                                <span style={{
                                                    backgroundColor: '#51cbce',
                                                    color: 'white',
                                                    padding: '5px 15px',
                                                    borderRadius: '20px',
                                                    fontSize: '14px',
                                                    fontWeight: 'bold'
                                                }}>
                                                    <i className="fas fa-file-invoice" style={{ marginRight: '6px' }}></i>
                                                    Yêu cầu xuất hóa đơn
                                                </span>
                                            </div>
                                            <FormGroup style={{ marginTop: '15px' }}>
                                                <div className="form-row">
                                                    <div className="form-group col-md-12">
                                                        <Label>Loại khách hàng</Label>
                                                        <div>
                                                            <span style={{
                                                                backgroundColor: orderData.invoiceInfo.customerType === 1 ? '#e8f4fd' : '#f0f4ff',
                                                                color: orderData.invoiceInfo.customerType === 1 ? '#0c5394' : '#3271FF',
                                                                padding: '5px 15px',
                                                                borderRadius: '20px',
                                                                fontSize: '14px',
                                                                fontWeight: 'bold'
                                                            }}>
                                                                {orderData.invoiceInfo.customerType === 1 ? 'Tổ chức / HKD' : 'Cá nhân'}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>

                                                {orderData.invoiceInfo.customerType === 1 && (
                                                    <div className="form-row">
                                                        <div className="form-group col-md-12">
                                                            <Label>Tên tổ chức</Label>
                                                            <Input value={editedCustomer.invoiceOrganization || ''}
                                                                onChange={(e) => handleCustomerChange('invoiceOrganization', e.target.value)} />
                                                        </div>
                                                    </div>
                                                )}

                                                <div className="form-row">
                                                    <div className="form-group col-md-12">
                                                        <Label>Mã số thuế</Label>
                                                        <Input value={orderData.invoiceInfo.customerType === 1
                                                            ? (editedCustomer.invoiceTaxCode || '')
                                                            : (editedCustomer.invoiceIdentificationNumber || '')}
                                                            onChange={(e) => handleCustomerChange(
                                                                orderData.invoiceInfo.customerType === 1 ? 'invoiceTaxCode' : 'invoiceIdentificationNumber',
                                                                e.target.value
                                                            )} />
                                                    </div>
                                                </div>
                                                <div className="form-row">
                                                    <div className="form-group col-md-12">
                                                        <Label>Địa chỉ</Label>
                                                        <Input value={editedCustomer.invoiceAddress || ''}
                                                            onChange={(e) => handleCustomerChange('invoiceAddress', e.target.value)} />
                                                    </div>
                                                </div>
                                            </FormGroup>
                                        </>
                                    )}
                                </CardBody>
                                {hasCustomerChanges() && (
                                    <CardFooter style={{ display: 'flex', justifyContent: 'flex-end', borderTop: '1px solid #eee', padding: '15px 20px' }}>
                                        <Button
                                            color="info"
                                            onClick={handleSaveCustomer}
                                            disabled={savingCustomer}
                                            style={{ borderRadius: '20px', padding: '8px 25px', fontWeight: 'bold' }}
                                        >
                                            {savingCustomer ? (
                                                <><i className="fas fa-spinner fa-spin" style={{ marginRight: '6px' }}></i>Đang lưu...</>
                                            ) : (
                                                <><i className="fas fa-save" style={{ marginRight: '6px' }}></i>Lưu thay đổi</>
                                            )}
                                        </Button>
                                    </CardFooter>
                                )}
                            </Card>
                        </Col>

                        <Col lg="8" md="8" sm="8">
                            <Card style={{ padding: '15px', borderRadius: '30px' }}>
                                <CardHeader>
                                    <Label style={{ fontSize: '24px', color: 'DimGray', fontWeight: 'bold' }}><i className="fas fa-file-invoice" style={{ marginRight: '8px' }}></i>Đơn hàng #{orderData?.id}</Label>
                                    <br />
                                    <Label className="text-success" style={{ fontSize: '16px', fontWeight: 'normal' }}>{orderData ? moment(orderData.createdAt).format('HH:mm:ss DD/MM/YYYY') : ''}</Label>
                                </CardHeader>

                                <CardBody>
                                    <FormGroup>
                                        <div className="form-row">
                                            <div className="form-group col-md-6">
                                                <Label for="status">Trạng thái</Label>
                                                <div
                                                    ref={dropdownRef}
                                                    style={{
                                                        position: "relative",
                                                        width: "100%"
                                                    }}
                                                >
                                                    <button
                                                        onClick={toggleDropdown}
                                                        disabled={updatingStatus}
                                                        style={{
                                                            backgroundColor: getStatusColor(orderData?.paymentStatus),
                                                            color: "white",
                                                            fontWeight: "bold",
                                                            textAlign: "center",
                                                            border: "none",
                                                            outline: "none",
                                                            boxShadow: "none",
                                                            borderRadius: "5px",
                                                            width: "100%",
                                                            padding: "10px 15px",
                                                            cursor: "pointer",
                                                            display: "flex",
                                                            justifyContent: "space-between",
                                                            alignItems: "center",
                                                            transition: "filter 0.2s ease"
                                                        }}
                                                        className="status-dropdown-button"
                                                        onMouseOver={(e) => e.currentTarget.style.filter = "brightness(90%)"}
                                                        onMouseOut={(e) => e.currentTarget.style.filter = "brightness(100%)"}
                                                    >
                                                        <span>{orderData?.paymentStatus}</span>
                                                        <i className="fa fa-caret-down"></i>
                                                    </button>

                                                    {dropdownOpen && (
                                                        <div
                                                            style={{
                                                                position: "absolute",
                                                                top: "calc(100% + 5px)",
                                                                left: 0,
                                                                right: 0,
                                                                backgroundColor: "#fff",
                                                                boxShadow: "0 2px 10px rgba(0,0,0,0.2)",
                                                                zIndex: 1000,
                                                                padding: 0,
                                                                overflow: "hidden",
                                                                borderRadius: "5px",
                                                                width: "100%"
                                                            }}
                                                        >
                                                            {getStatusOptions().map((option, index) => (
                                                                <div
                                                                    key={index}
                                                                    onClick={() => {
                                                                        handleUpdateStatus(option.value);
                                                                        setDropdownOpen(false);
                                                                    }}
                                                                    style={{
                                                                        backgroundColor: option.color,
                                                                        color: "white",
                                                                        fontWeight: "bold",
                                                                        textAlign: "center",
                                                                        padding: "12px 15px",
                                                                        cursor: "pointer",
                                                                        borderBottom: index < getStatusOptions().length - 1 ? "1px solid rgba(255,255,255,0.1)" : "none",
                                                                        transition: "filter 0.2s"
                                                                    }}
                                                                    className="custom-dropdown-item"
                                                                    onMouseOver={(e) => e.currentTarget.style.filter = "brightness(85%)"}
                                                                    onMouseOut={(e) => e.currentTarget.style.filter = "brightness(100%)"}
                                                                >
                                                                    {option.label}
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>

                                            <div className="form-group col-md-6">
                                                <Label for="created">Xử lý bởi</Label>
                                                <Input
                                                    value={orderData?.handleBy || ''}
                                                    id="created"
                                                />
                                            </div>
                                        </div>

                                        <div className="form-row">

                                            <div className="form-group col-md-6">
                                                <Label for="method">Phương thức thanh toán</Label>
                                                <Input
                                                    value={orderData?.paymentMethod || ''}
                                                    id="method"
                                                />
                                            </div>

                                            <div className="form-group col-md-6">
                                                <Label for="price">Tổng tiền</Label>
                                                <Input
                                                    value={orderData ? new Intl.NumberFormat('vi-VN').format(orderData.totalPrice) + ' đ' : ''}
                                                    id="price"
                                                />
                                            </div>
                                        </div>

                                    </FormGroup>

                                    <Row className="mt-4">
                                        <Col md="12">
                                            <div className="d-none d-md-block">
                                                <Table className="table-hover" style={{ lineHeight: "15px", width: "100%" }}>
                                                    <thead className="text-primary">
                                                        <tr>
                                                            <th>Phần mềm</th>
                                                            <th>Hành động</th>
                                                            <th>Email</th>
                                                            <th>Tên người dùng</th>
                                                            <th className="text-right">Đơn giá</th>
                                                            <th className="text-center">SL</th>
                                                            <th className="text-right">Thành tiền</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {toolsData && toolsData.length > 0 ? (
                                                            toolsData.map((tool, index) => (
                                                                <tr key={index}>
                                                                    <td>
                                                                        <strong style={{ fontSize: '16px', color: '#6bd098' }}>{tool.toolName}</strong>
                                                                        <br />
                                                                        <span style={{ color: '#6bd098', display: 'block', marginTop: '5px' }}>{tool.attributeString}</span>
                                                                    </td>
                                                                    <td>{tool.actionString}</td>
                                                                    <td>{tool.email}</td>
                                                                    <td>{tool.username}</td>
                                                                    <td className="text-right">{new Intl.NumberFormat('vi-VN').format(tool.price)} đ</td>
                                                                    <td className="text-center">{tool.quantity || 1}</td>
                                                                    <td className="text-right" style={{ fontWeight: 'bold' }}>
                                                                        {new Intl.NumberFormat('vi-VN').format((tool.price || 0) * (tool.quantity || 1))} đ
                                                                    </td>
                                                                </tr>
                                                            ))
                                                        ) : (
                                                            <tr>
                                                                <td colSpan="7" className="text-center">Không có dữ liệu phần mềm</td>
                                                            </tr>
                                                        )}
                                                    </tbody>
                                                </Table>
                                            </div>

                                            {/* Hiển thị cho thiết bị di động */}
                                            <div className="d-md-none">
                                                {toolsData && toolsData.length > 0 ? (
                                                    toolsData.map((tool, index) => (
                                                        <Card key={index} className="mb-3" style={{ borderRadius: '15px', boxShadow: '0 0 10px 0 rgba(0, 0, 0, 0)', border: '1px solid #51cbce' }}>
                                                            <CardHeader style={{ backgroundColor: '#f9f9f9', padding: '10px 15px', borderTopLeftRadius: '15px', borderTopRightRadius: '15px', borderBottom: '1px solid #51cbce' }}>
                                                                <div className="d-flex justify-content-between align-items-center">
                                                                    <strong style={{ fontSize: '16px', color: '#51cbce' }}>{tool.toolName}</strong>
                                                                    <span className="text-bold">
                                                                        {new Intl.NumberFormat('vi-VN').format(tool.price)} đ
                                                                    </span>
                                                                </div>
                                                                <div style={{ color: '#51cbce', marginTop: '5px', fontSize: '13px' }}>
                                                                    {tool.attributeString}
                                                                </div>
                                                            </CardHeader>
                                                            <CardBody style={{ padding: '10px 15px' }}>
                                                                <div className="d-flex justify-content-between mb-2">
                                                                    <span style={{ color: '#888' }}>Hành động:</span>
                                                                    <span>{tool.actionString}</span>
                                                                </div>
                                                                <div className="d-flex justify-content-between mb-2">
                                                                    <span style={{ color: '#888' }}>Số lượng:</span>
                                                                    <span>{tool.quantity || 1}</span>
                                                                </div>
                                                                <div className="d-flex justify-content-between mb-2">
                                                                    <span style={{ color: '#888' }}>Thành tiền:</span>
                                                                    <span style={{ fontWeight: 'bold' }}>{new Intl.NumberFormat('vi-VN').format((tool.price || 0) * (tool.quantity || 1))} đ</span>
                                                                </div>
                                                                {tool.version && (
                                                                    <div className="d-flex justify-content-between mb-2">
                                                                        <span style={{ color: '#888' }}>Phiên bản:</span>
                                                                        <span>{tool.version}</span>
                                                                    </div>
                                                                )}
                                                                {tool.expired && (
                                                                    <div className="d-flex justify-content-between mb-2">
                                                                        <span style={{ color: '#888' }}>Thời hạn:</span>
                                                                        <span>{tool.expired}</span>
                                                                    </div>
                                                                )}
                                                            </CardBody>
                                                        </Card>
                                                    ))
                                                ) : (
                                                    <div className="text-center p-3">Không có dữ liệu phần mềm</div>
                                                )}
                                            </div>

                                            {/* Tổng kết đơn hàng */}
                                            <div style={{
                                                marginTop: '15px',
                                                padding: '15px 20px',
                                                backgroundColor: '#f9f9f9',
                                                borderRadius: '15px',
                                                border: '1px solid #eee'
                                            }}>
                                                {(() => {
                                                    const subtotal = toolsData?.reduce((sum, t) => sum + (t.price || 0) * (t.quantity || 1), 0) || 0;
                                                    const discountPrice = orderData?.discountPrice || 0;
                                                    const discountRate = orderData?.discountRate || 0;
                                                    const couponDiscount = orderData?.couponDiscount || 0;
                                                    const total = orderData?.totalPrice || 0;
                                                    return (
                                                        <>
                                                            <div className="d-flex justify-content-between mb-2">
                                                                <span style={{ color: '#888' }}>Tạm tính</span>
                                                                <span>{new Intl.NumberFormat('vi-VN').format(subtotal)} đ</span>
                                                            </div>
                                                            {discountPrice > 0 && (
                                                                <div className="d-flex justify-content-between mb-2">
                                                                    <span style={{ color: '#ef8157' }}>
                                                                        <i className="fas fa-tag" style={{ marginRight: '6px' }}></i>
                                                                        Giảm giá {discountRate > 0 ? `(${discountRate}%)` : ''}
                                                                    </span>
                                                                    <span style={{ color: '#ef8157' }}>-{new Intl.NumberFormat('vi-VN').format(discountPrice)} đ</span>
                                                                </div>
                                                            )}
                                                            {couponDiscount > 0 && (
                                                                <div className="d-flex justify-content-between mb-2">
                                                                    <span style={{ color: '#6bd098' }}>
                                                                        <i className="fas fa-ticket-alt" style={{ marginRight: '6px' }}></i>
                                                                        Mã giảm giá {orderData?.couponCode ? `(${orderData.couponCode})` : ''}
                                                                    </span>
                                                                    <span style={{ color: '#6bd098' }}>-{new Intl.NumberFormat('vi-VN').format(couponDiscount)} đ</span>
                                                                </div>
                                                            )}
                                                            <hr style={{ margin: '10px 0' }} />
                                                            <div className="d-flex justify-content-between">
                                                                <span style={{ fontSize: '18px', fontWeight: 'bold', color: '#333' }}>Tổng tiền</span>
                                                                <span style={{ fontSize: '18px', fontWeight: 'bold', color: '#51cbce' }}>
                                                                    {new Intl.NumberFormat('vi-VN').format(total)} đ
                                                                </span>
                                                            </div>
                                                        </>
                                                    );
                                                })()}
                                            </div>
                                        </Col>
                                    </Row>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>

                    {
                        botActionData && botActionData.length > 0 && (
                            <Row className="mt-4">
                                <Col md="12">
                                    <Card style={{ padding: '15px', borderRadius: '30px' }}>
                                        <CardHeader>
                                            <Label style={{ fontSize: '24px', color: 'DimGray', fontWeight: 'bold' }}><i className="fas fa-cogs" style={{ marginRight: '8px' }}></i>Thao tác</Label>
                                        </CardHeader>

                                        <CardBody>
                                            <Table className="table-hover d-none d-md-table" style={{ lineHeight: "15px", width: "100%" }}>
                                                <thead className="text-primary">
                                                    <tr>
                                                        <th>Thời gian</th>
                                                        <th>Phần mềm</th>
                                                        <th>Người thực hiện</th>
                                                        <th>Hành động</th>
                                                        <th>Trạng thái</th>
                                                        <th>Mô tả</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {botActionData.map((action, index) => (
                                                        <tr
                                                            key={index}
                                                            style={{
                                                                backgroundColor: getActionStatusBgColor(action.status),
                                                            }}
                                                        >
                                                            <td>{moment(action.createdAt).format('HH:mm:ss DD/MM/YYYY')}</td>
                                                            <td>{action.toolName}</td>
                                                            <td>{action.fullName}</td>
                                                            <td>{action.action}</td>
                                                            <td style={{
                                                                color: getActionStatusColor(action.status),
                                                                fontWeight: 'bold'
                                                            }}>{action.status}</td>
                                                            <td>
                                                                {action.message}
                                                                {action.error && (
                                                                    <div style={{ marginTop: '8px' }}>
                                                                        <span style={{ color: '#888', fontSize: '12px' }}>{action.error}</span>
                                                                    </div>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </Table>

                                            {/* Hiển thị cho thiết bị di động */}
                                            <div className="d-md-none">
                                                {botActionData.map((action, index) => (
                                                    <Card
                                                        key={index}
                                                        className="mb-3"
                                                        style={{
                                                            borderRadius: '15px',
                                                            boxShadow: '0 0 10px 0 rgba(0, 0, 0, 0)',
                                                            border: '1px solid #51cbce',
                                                            backgroundColor: getActionStatusBgColor(action.status)
                                                        }}
                                                    >
                                                        <CardHeader style={{ backgroundColor: '#f9f9f9', padding: '10px 15px', borderTopLeftRadius: '15px', borderTopRightRadius: '15px', borderBottom: '1px solid #51cbce' }}>
                                                            <strong style={{ color: '#51cbce' }}>{action.action}</strong>
                                                        </CardHeader>
                                                        <CardBody style={{ padding: '10px 15px' }}>
                                                            <div className="d-flex justify-content-between mb-2">
                                                                <span style={{ color: '#888' }}>Phần mềm:</span>
                                                                <span>{action.toolName}</span>
                                                            </div>
                                                            <div className="d-flex justify-content-between mb-2">
                                                                <span style={{ color: '#888' }}>Thời gian:</span>
                                                                <span>{moment(action.createdAt).format('HH:mm:ss DD/MM/YYYY')}</span>
                                                            </div>
                                                            <div className="d-flex justify-content-between mb-2">
                                                                <span style={{ color: '#888' }}>Người thực hiện:</span>
                                                                <span>{action.fullName}</span>
                                                            </div>
                                                            <div className="d-flex justify-content-between mb-2">
                                                                <span style={{ color: '#888' }}>Trạng thái:</span>
                                                                <span style={{ color: getActionStatusColor(action.status) }}>{action.status}</span>
                                                            </div>
                                                            <div className="d-flex justify-content-between mb-2" style={{ marginTop: '10px' }}>
                                                                <span>{action.message}</span>
                                                            </div>
                                                            {action.error && (
                                                                <div style={{ marginTop: '8px', color: '#888', fontSize: '12px' }}>
                                                                    {action.error}
                                                                </div>
                                                            )}
                                                        </CardBody>
                                                    </Card>
                                                ))}
                                            </div>
                                        </CardBody>
                                    </Card>
                                </Col>
                            </Row>
                        )
                    }

                    {userRole === 'ADMIN' && (
                        <Row className="mt-3">
                            <Col md="12" className="text-right">
                                <Button
                                    color="danger"
                                    onClick={() => {
                                        Swal.fire({
                                            title: 'Xóa đơn hàng?',
                                            text: `Bạn có chắc muốn xóa đơn hàng #${id}? Hành động này không thể hoàn tác.`,
                                            icon: 'warning',
                                            showCancelButton: true,
                                            confirmButtonColor: '#dc3545',
                                            cancelButtonColor: '#888',
                                            confirmButtonText: 'Xóa',
                                            cancelButtonText: 'Hủy'
                                        }).then(async (result) => {
                                            if (result.isConfirmed) {
                                                try {
                                                    await OrderService.deleteOrder(id);
                                                    Swal.fire('Đã xóa!', 'Đơn hàng đã được xóa.', 'success');
                                                    history.push('/don-hang');
                                                } catch (err) {
                                                    Swal.fire('Lỗi', 'Không thể xóa đơn hàng.', 'error');
                                                }
                                            }
                                        });
                                    }}
                                    style={{ borderRadius: '20px', padding: '8px 25px', fontWeight: 'bold' }}
                                >
                                    <i className="fas fa-trash" style={{ marginRight: '6px' }}></i>Xóa đơn hàng
                                </Button>
                            </Col>
                        </Row>
                    )}
                </>
            )}
        </div>
    );
}

export default OrderIDDetails; 